import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { apiCallStatus, kpiTypes } from 'helpers/constants';
import { GeneralSelect, InfoMessage, PopOutChart } from 'sharedComponents';
import LoadingStatusWrapper from 'sharedComponents/WithLoadingStatus/LoadingStatusWrapper';
import ErrorView from 'sharedComponents/ErrorBoundary/ErrorView';
import { useReportIssue } from 'sharedHooks/useReportIssue';
import useChartConfig, { chartPlugins } from './useChartConfig';
import { opButtons } from '../../constants';
import useReloadTab from '../../useReloadTab';
import { PopOutButton, ResetZoomButton, withLeftButtonGroup, withPopOutRow, withRightButtonGroup } from 'sharedComponents/Chart/headers';

import './style.scss';
import { getTranslatedHistoScatterV2KPIName } from 'helpers/translateHelper';
import useChartResize from 'sharedHooks/useChartResize';

const ScatterPlotComponent = ({
    model: {
        dateSelection,
        scatterData,
        scatterDataConfiguration,
        scatterDataLoadingStatus,
        scatterKpis,
        scatterKpisLoadingStatus,
        selectedKpi,
        errorData,
    },
    actions: {
        getScatterForAsset,
        getScatterKPIsForAsset,
        setSelectedKpi,
        removeSelectedKpiLegend
    },
    activeTab,
    asset,
}) => {
    const { t: translate } = useTranslation();
    const { showReportIssue } = useReportIssue();
    const [isZoomed, setIsZoomed] = useState(false);
    const [isForceRefresh, setForceRefresh] = useState(false);
    const containerRef = useRef();
    const chartRef = useRef();

    useChartResize(containerRef, chartRef);
    const refreshData = () => {
        if (scatterKpisLoadingStatus === apiCallStatus.SUCCESS &&
            activeTab === opButtons.scatterPlotV2 &&
            scatterKpis.length &&
            selectedKpi &&
            dateSelection.from &&
            dateSelection.to) {
            getScatterForAsset(dateSelection.from, dateSelection.to, asset, selectedKpi);
        }
    };

    useEffect(() => {
        setIsZoomed(false);
    }, [dateSelection]);

    useReloadTab(refreshData,
        opButtons.scatterPlotV2,
        [selectedKpi, scatterKpisLoadingStatus, dateSelection]);

    // After navaigate to other asset, Signal data will refresh Once moved to scatterplot Tab.
    useEffect(() => {
        if (activeTab === opButtons.scatterPlotV2 && isForceRefresh) {
            refreshData();
            setForceRefresh(false);
        }
    }, [activeTab]);

    useEffect(() => {
        setForceRefresh(true);
    }, [asset]);

    useEffect(() => {
        scatterKpisLoadingStatus !== apiCallStatus.SUCCESS &&
            getScatterKPIsForAsset(asset, kpiTypes.SCATTER);
    }, []);

    const options = useChartConfig(scatterData, scatterDataConfiguration, scatterKpis, removeSelectedKpiLegend, asset);

    const ScatterKPISelector = () => {
        return <GeneralSelect
            id='scatter_chart_type_selector'
            placeholder={translate('ABB.Powertrain.Frontend.pleaseSelectKPI')}
            options={scatterKpis}
            value={selectedKpi || ''}
            onChange={setSelectedKpi}
            getOptionLabel={option => getTranslatedHistoScatterV2KPIName(option)}
            getOptionValue={option => option.scatterKey} />;
    };

    const MainChartHeader = withPopOutRow([
        withLeftButtonGroup([ScatterKPISelector]),
        withRightButtonGroup([ResetZoomButton, PopOutButton])
    ]);

    const PopOutChartHeader = withPopOutRow([
        withRightButtonGroup([ResetZoomButton, PopOutButton])
    ]);

    if (errorData?.loadingStatus === apiCallStatus.ERROR) {
        return <ErrorView
            errorCode={errorData.errorCode}
            onRetry={() => getScatterKPIsForAsset(asset, kpiTypes.SCATTER)}
            onReportIssue={showReportIssue}
        />;
    }

    return scatterKpisLoadingStatus === apiCallStatus.SUCCESS && scatterKpis.length === 0 ?
        <Row className='button-row-container'>
            <Col className='table-empty-content'>
                <InfoMessage title='ABB.Powertrain.Frontend.noSupportedMeasurementTypes' />
            </Col>
        </Row> :
        <LoadingStatusWrapper
            useCustomAnchor
            transparent
            loadingStatus={[scatterKpisLoadingStatus, scatterDataLoadingStatus]}>
            <div ref={containerRef} className='scatter-chart-container'>
                <PopOutChart
                    chartRef={chartRef}
                    isZoomed={isZoomed}
                    setIsZoomed={setIsZoomed}
                    hasKpi={!!selectedKpi}
                    renderNoKpi={() => <InfoMessage
                        title='ABB.Powertrain.Frontend.chartNoKPIsSelected'
                        text='ABB.Powertrain.Frontend.chartNoKPIsSelectedUserGuide' />}
                    hasData={!!scatterData?.length}
                    renderNoData={() => <InfoMessage
                        title='ABB.Powertrain.Frontend.noDataLabelAvailable'
                        text='ABB.Powertrain.Frontend.adjustDateRangeOrChartType' />}
                    options={options}
                    modules={chartPlugins}
                    loadingStatus={scatterDataLoadingStatus}
                    container='op-scatter-chart'
                    mainChartHeader={MainChartHeader}
                    popOutChartHeader={PopOutChartHeader}
                    isNotPreserveSignals={true}
                />
            </div>
        </LoadingStatusWrapper >;
};

export default ScatterPlotComponent;

ScatterPlotComponent.propTypes = {
    actions: PropTypes.shape({
        getScatterForAsset: PropTypes.func,
        getScatterKPIsForAsset: PropTypes.func,
        setSelectedKpi: PropTypes.func,
        removeSelectedKpiLegend: PropTypes.func,
    }),
    model: PropTypes.shape({
        activeDetailedInformationTab: PropTypes.string,
        dateSelection: PropTypes.shape({
            from: PropTypes.string,
            to: PropTypes.string
        }),
        selectedKpi: PropTypes.object,
        scatterData: PropTypes.array,
        scatterDataConfiguration: PropTypes.object,
        scatterDataLoadingStatus: PropTypes.string,
        scatterKpis: PropTypes.array,
        scatterKpisLoadingStatus: PropTypes.string,
        errorData: PropTypes.shape({
            loadingStatus: PropTypes.string,
            errorCode: PropTypes.number,
        }),
    }),
    activeTab: PropTypes.string,
    asset: PropTypes.object,
};
